import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useFela } from 'react-fela';
import { FormattedMessage } from 'react-intl';

import { useCopyToClipboard } from '../../../hooks/useCopyToClipboard';
import { Button } from '../../Button';
import { Icon } from '../../Icon';
import { Text as UIText } from '../../typography/Text';
import type { CopyButtonProps } from '../CopyButton';
import type { TextProps } from '../Text';
import * as felaRules from './Secret.rules';

export interface SecretProps extends TextProps {
    type: NonNullable<Exclude<CopyButtonProps['type'], 'text'>>;
    mask?: ReactNode;
}

export const Secret = ({ children, text, disabled = false, type, mask }: SecretProps) => {
    const [show, setShow] = useState(false);
    const { css } = useFela({ disabled, type, show });

    const { handleCopy } = useCopyToClipboard();

    const toggleShowText = useCallback(() => {
        setShow(s => !s);
    }, []);

    const displayedContent = useMemo(() => {
        if (show) return children ?? text;

        if (mask) return mask;

        if (type !== 'paymentCardNumber') return <FormattedMessage id={`crypted.${type}`} />;
    }, [show, children, text, mask, type]);

    return (
        <div className={css(felaRules.container)}>
            <div className={css(felaRules.secretContainer)}>
                <UIText
                    elementType={typeof displayedContent === 'string' ? 'p' : 'div'}
                    customStyle={show ? felaRules.text : felaRules.cryptedText}
                >
                    {displayedContent}
                </UIText>
                {disabled ? null : (
                    <Icon
                        onClick={toggleShowText}
                        type={!show ? 'eye' : 'eye-close'}
                        color={!show ? 'accentPrimary' : 'accentSecondary'}
                        width={24}
                        height={24}
                        customStyle={felaRules.icon}
                    />
                )}
            </div>
            <Button data-testid={`copy-button-${type}`} type='default' size='small' disabled={disabled} onClick={() => handleCopy(text)}>
                <FormattedMessage id='copy' />
            </Button>
            {disabled && (
                <UIText variant='LabelSmall' color='foregroundsTertiary' customStyle={felaRules.disabledWarningText}>
                    <FormattedMessage id='secret.password.autofillOnlyWarning' />
                </UIText>
            )}
        </div>
    );
};
